import React, { useCallback, useState } from "react";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import apiGd from "services/apiGd";

// Botão + modal para apagar EM MASSA (a partir do admin) o cardápio, todos os
// insumos e/ou todos os produtos intermediários de um restaurante.
export default function BulkDeleteInputsModal({ restaurantId, token, notify }) {
  const [isOpen, setIsOpen] = useState(false);
  const [deleteMenu, setDeleteMenu] = useState(false);
  const [deleteInputs, setDeleteInputs] = useState(false);
  const [deleteIntermediaries, setDeleteIntermediaries] = useState(false);
  const [loading, setLoading] = useState(false);

  const reset = useCallback(() => {
    setDeleteMenu(false);
    setDeleteInputs(false);
    setDeleteIntermediaries(false);
    setLoading(false);
  }, []);

  const toggle = useCallback(() => {
    setIsOpen((prev) => {
      if (prev) reset();
      return !prev;
    });
  }, [reset]);

  const handleToggleMenu = useCallback(() => {
    setDeleteMenu((prev) => !prev);
  }, []);

  // Insumos marcados => intermediários obrigatoriamente marcados (vínculo).
  const handleToggleInputs = useCallback(() => {
    setDeleteInputs((prev) => {
      const next = !prev;
      if (next) setDeleteIntermediaries(true);
      return next;
    });
  }, []);

  const handleToggleIntermediaries = useCallback(() => {
    // Não pode desmarcar intermediários enquanto insumos estiver marcado.
    if (deleteInputs) return;
    setDeleteIntermediaries((prev) => !prev);
  }, [deleteInputs]);

  const canSubmit =
    (deleteMenu || deleteInputs || deleteIntermediaries) && !loading;

  const handleConfirm = useCallback(async () => {
    if (!deleteMenu && !deleteInputs && !deleteIntermediaries) {
      notify("tr", "danger", "Selecione ao menos uma opção para apagar.");
      return;
    }

    setLoading(true);
    const messages = [];
    try {
      const reqConfig = { headers: { Authorization: `Bearer ${token}` } };

      if (deleteMenu) {
        await apiGd.put(
          `admin/restaurants/menu/delete`,
          { restaurant_id: Number(restaurantId) },
          reqConfig
        );
        messages.push("cardápio apagado");
      }

      if (deleteInputs || deleteIntermediaries) {
        const response = await apiGd.post(
          `/admin/restaurants/inputs-intermediaries/bulk-delete`,
          {
            restaurant_id: Number(restaurantId),
            delete_inputs: deleteInputs,
            delete_intermediaries: deleteIntermediaries,
          },
          reqConfig
        );

        const { inputs = 0, intermediaries = 0 } = response.data || {};
        messages.push(
          `${intermediaries} intermediário(s) e ${inputs} insumo(s) apagados`
        );
      }

      notify("tr", "success", `Concluído: ${messages.join("; ")}.`);
      reset();
      setIsOpen(false);
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Erro ao apagar cardápio/estoque.";
      notify("tr", "danger", msg);
      setLoading(false);
    }
  }, [
    deleteMenu,
    deleteInputs,
    deleteIntermediaries,
    restaurantId,
    token,
    notify,
    reset,
  ]);

  const optionRowStyle = {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    padding: "12px 14px",
    border: "1px solid #e9ecef",
    borderRadius: 8,
    marginBottom: 10,
  };

  return (
    <>
      <Button
        onClick={toggle}
        style={{
          float: "right",
          marginRight: 10,
          backgroundColor: "#7A0C10",
          borderRadius: 8,
        }}
      >
        Apagar cardápio e estoque
      </Button>

      <Modal isOpen={isOpen} toggle={toggle} size="lg">
        <ModalHeader toggle={toggle}>
          Apagar cardápio e estoque
        </ModalHeader>
        <ModalBody>
          <div
            style={{
              background: "#FDEDEE",
              border: "1px solid #F5C2C7",
              borderRadius: 8,
              padding: "12px 14px",
              color: "#842029",
              fontSize: 13.5,
              lineHeight: 1.5,
              marginBottom: 16,
            }}
          >
            <b>Atenção — ação em massa e destrutiva.</b> Isto apaga os itens
            selecionados de <b>todo o restaurante</b>. Os itens são marcados como
            deletados/cancelados e seus vínculos (categorias, produtos,
            complementos e composições) são removidos. Não há desfazer por aqui.
          </div>

          {/* Cardápio */}
          <div
            onClick={handleToggleMenu}
            role="button"
            tabIndex={0}
            style={{ ...optionRowStyle, cursor: "pointer" }}
          >
            <input
              type="checkbox"
              checked={deleteMenu}
              readOnly
              style={{ marginTop: 3, pointerEvents: "none" }}
            />
            <div>
              <div style={{ fontWeight: 600, color: "#333" }}>
                Apagar cardápio
              </div>
              <div style={{ fontSize: 12.5, color: "#6c6c6c" }}>
                Remove categorias, produtos e complementos do restaurante (e seus
                vínculos de iFood/EAN). Independente das opções de estoque.
              </div>
            </div>
          </div>

          {/* Insumos */}
          <div
            onClick={handleToggleInputs}
            role="button"
            tabIndex={0}
            style={{ ...optionRowStyle, cursor: "pointer" }}
          >
            <input
              type="checkbox"
              checked={deleteInputs}
              readOnly
              style={{ marginTop: 3, pointerEvents: "none" }}
            />
            <div>
              <div style={{ fontWeight: 600, color: "#333" }}>
                Apagar todos os insumos
              </div>
              <div style={{ fontSize: 12.5, color: "#6c6c6c" }}>
                Remove todos os insumos do restaurante e seus vínculos com
                produtos e complementos. Como os produtos intermediários são
                compostos por insumos, marcar esta opção <b>também apaga todos
                os intermediários</b> automaticamente.
              </div>
            </div>
          </div>

          {/* Produtos intermediários */}
          <div
            onClick={handleToggleIntermediaries}
            style={{
              ...optionRowStyle,
              cursor: deleteInputs ? "not-allowed" : "pointer",
              opacity: deleteInputs ? 0.7 : 1,
              marginBottom: 0,
            }}
          >
            <input
              type="checkbox"
              checked={deleteIntermediaries}
              disabled={deleteInputs}
              readOnly
              style={{ marginTop: 3, pointerEvents: "none" }}
            />
            <div>
              <div style={{ fontWeight: 600, color: "#333" }}>
                Apagar todos os produtos intermediários
              </div>
              <div style={{ fontSize: 12.5, color: "#6c6c6c" }}>
                Remove todos os produtos intermediários e seus vínculos. Pode ser
                usada sozinha (mantém os insumos).
                {deleteInputs && (
                  <>
                    {" "}
                    <b>Obrigatório</b> quando "Apagar todos os insumos" está
                    marcado.
                  </>
                )}
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle} disabled={loading}>
            Cancelar
          </Button>
          <Button color="danger" onClick={handleConfirm} disabled={!canSubmit}>
            {loading ? "Apagando..." : "Apagar selecionados"}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
